import { App } from "antd";
import styles from "./DatasetSnapshots.module.scss";
import {
  useCloneFromSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdClonePost,
  useDeleteSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdDelete,
  useRestoreSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdRestorePost,
} from "../../../../vl-api/vlComponents";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "../../../../vl-api/vlUtils";
import { ModalFuncProps } from "antd/es/modal/interface";
import { useDispatch } from "react-redux";
import { fetchDataset } from "../../../../redux/SingleDataset/actions";
import { Link } from "react-router-dom";
import { copyToClipboard } from "helpers/utility/utilities";
import { useUserConfig } from "../../../../hooks/useUserConfig";

export const useSnapshotItem = (
  datasetId: string,
  snapshotId: string,
  onClose: () => void,
) => {
  const { modal, notification, message } = App.useApp();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { refetch: refetchUserConfig } = useUserConfig();

  const refetchSnapshots = () =>
    invalidateActiveQueryByOperationId(
      queryClient,
      "listSnapshotsApiV1DatasetDatasetIdSnapshotGet",
    );

  const showProgressNotification = (action: string) =>
    notification.success({
      message: (
        <div className={styles.notification}>
          {action} in progress, you can track the progress in the task manager.
        </div>
      ),
      description: (
        <Link to="/task-manager" onClick={() => notification.destroy()}>
          View in task manager
        </Link>
      ),
    });

  const onError = (error: any) => {
    modal.error({
      title: "Operation failed",
      content: error.detail,
      autoFocusButton: null,
    });
  };

  const cloneSnapshotMutation =
    useCloneFromSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdClonePost({
      onSuccess: () => {
        refetchSnapshots();
        onClose();
        showProgressNotification("Cloning");
      },
      onError,
    });

  const restoreSnapshotMutation =
    useRestoreSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdRestorePost({
      onSuccess: () => {
        refetchSnapshots();
        dispatch(fetchDataset(datasetId) as any);
        refetchUserConfig();
        onClose();
        showProgressNotification("Restoring");
      },
      onError,
    });

  const deleteSnapshotMutation =
    useDeleteSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdDelete({
      onSuccess: () => {
        refetchSnapshots();
        notification.success({ message: "Snapshot deleted successfully." });
      },
      onError,
    });

  const showConfirmation = ({
    title,
    content,
    onOk,
  }: Pick<ModalFuncProps, "title" | "content" | "onOk">) =>
    modal.confirm({
      title,
      content,
      onOk,
      okText: "Yes",
      okButtonProps: { size: "middle" },
      cancelButtonProps: { type: "link", size: "middle" },
      autoFocusButton: null,
    });

  const handleRemoveClick = (name: string) => {
    showConfirmation({
      title: "Remove Snapshot",
      content: `Are you sure you want to remove ${name}?`,
      onOk: () =>
        deleteSnapshotMutation.mutate({
          pathParams: { datasetId, snapshotId },
        }),
    });
  };

  const handleRestoreClick = () => {
    showConfirmation({
      title: "Restore Snapshot",
      content:
        "This will revert the dataset to the exact state saved in this snapshot",
      onOk: () =>
        restoreSnapshotMutation.mutate({
          pathParams: { snapshotId, datasetId },
        }),
    });
  };

  const handleCloneClick = () => {
    showConfirmation({
      title: "Clone Snapshot",
      content: "This action will create a new dataset out of this snapshot",
      onOk: () =>
        cloneSnapshotMutation.mutate({
          pathParams: { datasetId, snapshotId },
        }),
    });
  };

  const handleCopyClick = async () => {
    try {
      copyToClipboard(snapshotId);
      message.success("Snapshot ID copied to clipboard!");
    } catch (error) {
      message.error("Failed to copy to clipboard");
    }
  };

  return {
    handleRemoveClick,
    handleRestoreClick,
    handleCloneClick,
    handleCopyClick,
  };
};
